'use client';

import React, { useCallback, useId, useState } from 'react';
import { useTranslations } from 'use-intl';
import Button from '@/components/atoms/button';
import Checkbox from '@/components/atoms/checkbox';
import Input from '@/components/atoms/input';
import ResponsiveModal from '@/components/organisms/responsive-modal';
import type { ClearCartResult } from '@/lib/hooks/useCart/types';

export type EmptyCartDialogProps = {
  isOpen: boolean;
  /** EWM-653 — save-to-list is Master-only; hides the checkbox for view-only roles. */
  canSaveToList?: boolean;
  onClose: () => void;
  /**
   * Retires the cart server-side — cart/clearCart. EWM-929: resolves the BFF's
   * outcome instead of void, because the retire can legitimately be refused
   * (checkout lock, missing permission, upstream failure) and the dialog must
   * not close over an untouched cart.
   */
  onClear: () => Promise<ClearCartResult>;
  /** Creates a purchase list with the given name and copies every cart line into it. */
  onSaveToNewList?: (name: string) => Promise<boolean>;
};

// EWM-791 — the "Empty cart" confirmation. Emptying retires the cart (the BFF
// deletes it, or freezes it when it carries payments, and the next fetch starts
// a fresh one), so the dialog offers to preserve the basket as a purchase list
// first. A failed save BLOCKS the clear — the customer never loses items to a
// half-completed save.
//
// EWM-929 — and a failed CLEAR blocks the close. The dialog used to close on
// any resolution of `onClear`, so a commercetools rejection (the 400 this
// ticket is about) or an EWM-552 checkout lock left the customer looking at an
// empty page over a cart that still had everything in it. On a refusal the
// dialog now stays open, names the reason, and offers the way forward Mehdi
// asked for: save the basket to a purchase list instead (or retry, for the
// roles that have no purchase lists) — unless the basket was already saved on
// the way in, in which case the save is neither offered nor repeated and the
// alert says so.
const EmptyCartDialog = ({
  isOpen,
  canSaveToList = false,
  onClose,
  onClear,
  onSaveToNewList,
}: EmptyCartDialogProps) => {
  const translate = useTranslations();
  const titleId = useId();

  const [saveFirst, setSaveFirst] = useState(false);
  const [listName, setListName] = useState('');
  const [processing, setProcessing] = useState(false);
  const [saveFailed, setSaveFailed] = useState(false);
  // The list the basket has ALREADY been saved to, or null. Once a save-first
  // has succeeded the list exists: offering the save again would create a
  // duplicate one, and the "you can save them instead" copy is no longer true.
  const [savedListName, setSavedListName] = useState<string | null>(null);
  // Null until the server has refused a clear; then it holds WHY, so the alert
  // can distinguish the checkout lock from every other refusal.
  const [clearFailure, setClearFailure] = useState<Extract<ClearCartResult, { success: false }> | null>(null);

  const canOfferList = canSaveToList && !!onSaveToNewList;
  const alreadySaved = savedListName !== null;
  // Saving is the way out of a refusal only while the items are NOT saved yet.
  const offerSaveInstead = !!clearFailure && canOfferList && !alreadySaved;

  const resetAndClose = useCallback(() => {
    setSaveFirst(false);
    setListName('');
    setSaveFailed(false);
    setSavedListName(null);
    setClearFailure(null);
    onClose();
  }, [onClose]);

  const saveToList = useCallback(async (): Promise<boolean> => {
    if (!onSaveToNewList) return true;
    const name = listName.trim() || translate('cart.empty-cart-default-list-name');
    // Fail closed: a save that throws or rejects must read as "not saved"
    // (which BLOCKS the clear), never as saved.
    let saved = false;
    try {
      saved = await onSaveToNewList(name);
    } catch {
      saved = false;
    }
    if (saved) setSavedListName(name);
    else setSaveFailed(true);
    return saved;
  }, [listName, onSaveToNewList, translate]);

  const handleConfirm = useCallback(async () => {
    setProcessing(true);
    setSaveFailed(false);
    setClearFailure(null);
    try {
      // `savedListName` short-circuits the retry after a refused clear: the
      // list was created on the first attempt and must not be created twice.
      if (saveFirst && onSaveToNewList && !savedListName) {
        if (!(await saveToList())) return;
      }

      const result = await onClear();
      if (!result.success) {
        // Cart untouched — keep the dialog open with the reason.
        setClearFailure(result);
        return;
      }

      resetAndClose();
    } catch {
      // Nothing on this path is expected to reject — the SDK resolves HTTP
      // errors into a ClearCartResult — but if one ever does, the cart's state
      // is unknown. Fail closed: keep the dialog open with the generic refusal
      // rather than close over items that are probably still there.
      setClearFailure({ success: false });
    } finally {
      setProcessing(false);
    }
  }, [saveFirst, onSaveToNewList, savedListName, saveToList, onClear, resetAndClose]);

  // The fallback offered after a refused clear: keep the items by saving them
  // to a purchase list. Deliberately does NOT retry the clear afterwards — the
  // cart is meant to stay exactly as it is.
  const handleSaveInstead = useCallback(async () => {
    setProcessing(true);
    setSaveFailed(false);
    try {
      if (await saveToList()) resetAndClose();
    } finally {
      setProcessing(false);
    }
  }, [saveToList, resetAndClose]);

  const failureMessage = clearFailure
    ? translate(offerSaveInstead ? 'cart.empty-cart-failed-save-option' : 'cart.empty-cart-failed')
    : undefined;

  return (
    <ResponsiveModal
      className="lg:max-w-[440px]"
      isOpen={isOpen}
      onRequestClose={resetAndClose}
      closeButton
      aria={{ labelledby: titleId }}
    >
      <div className="px-6">
        <h4 id={titleId} className="text-20 pt-6 pb-4 font-semibold text-gray-800">
          {translate('cart.empty-cart-title')}
        </h4>
        {/* Once the clear has been refused the alert below carries the whole
            story; repeating "all items will be removed" would contradict it. */}
        {!clearFailure && <p className="text-14 text-gray-700">{translate('cart.empty-cart-desc')}</p>}

        {canOfferList && !alreadySaved && (
          <div className="mt-4 grid gap-3">
            {/* The save-FIRST option only makes sense while the clear is still
                on the table; after a refusal, saving IS the offered action, so
                the name field stands on its own. */}
            {!clearFailure && (
              <Checkbox
                label={translate('cart.empty-cart-save-first')}
                checked={saveFirst}
                onChecked={setSaveFirst}
                disabled={processing}
              />
            )}
            {(clearFailure || saveFirst) && (
              <Input
                id="empty-cart-list-name"
                name="empty-cart-list-name"
                label={translate('cart.empty-cart-list-name')}
                value={listName}
                placeholder={translate('cart.empty-cart-default-list-name')}
                onChange={(e) => setListName(e.target.value)}
                disabled={processing}
              />
            )}
          </div>
        )}

        {failureMessage && (
          <div role="alert" className="mt-4">
            <p className="text-14 text-red-500">{failureMessage}</p>
            {/* The dialog is the save's only feedback surface, so a refusal
                that follows a successful save has to say where the items went. */}
            {savedListName && (
              <p className="text-14 mt-1 text-gray-700">
                {translate('cart.empty-cart-already-saved', { name: savedListName })}
              </p>
            )}
          </div>
        )}

        {saveFailed && (
          <p role="alert" className="text-14 mt-4 text-red-500">
            {translate('cart.empty-cart-save-failed')}
          </p>
        )}
      </div>

      <div className="mt-5 flex items-center justify-end gap-3 border-t border-neutral-400 p-6">
        <Button variant="secondary" size="m" className="min-w-[112px]" onClick={resetAndClose} disabled={processing}>
          {translate('common.cancel')}
        </Button>

        {!clearFailure && (
          <Button variant="warning" size="m" className="min-w-[112px]" onClick={handleConfirm} loading={processing}>
            {translate('cart.empty-cart-confirm')}
          </Button>
        )}

        {offerSaveInstead && (
          <Button variant="primary" size="m" className="min-w-[112px]" onClick={handleSaveInstead} loading={processing}>
            {translate('cart.empty-cart-save-to-list')}
          </Button>
        )}

        {clearFailure && !offerSaveInstead && (
          <Button variant="warning" size="m" className="min-w-[112px]" onClick={handleConfirm} loading={processing}>
            {translate('cart.empty-cart-retry')}
          </Button>
        )}
      </div>
    </ResponsiveModal>
  );
};

export default EmptyCartDialog;
